// Public marketing page shown when the user is not yet signed in. Click
// "Sign in" to surface the PasswordGate. Designed to brief a prospect and
// give them the price point + value prop in under 30 seconds.

const FEATURES = [
  {
    title: 'Six-module AI pipeline',
    body: 'Fact extraction, 11-area QA, ranked red flags, 8-dimension defensibility, 0–100 score, and a regulator-ready report. Each module is purpose-built — not a single mega-prompt.',
  },
  {
    title: 'Defensibility, not vibes',
    body: 'Output cites MLR 2017, JMLSG, and FATF references the model actually used. PASS only when evidence is unambiguous. Designed for a Section 166 reviewer, not a marketing demo.',
  },
  {
    title: 'PDF + Word, regulator-ready',
    body: 'Branded cover page, score card, decision rationale, all 9 sections, confidentiality footer, page numbering. Hand it to your MLRO.',
  },
  {
    title: 'Browser-side OCR for scans',
    body: 'Drop a scanned PDF — Tesseract.js converts it locally. No document data leaves the browser. Privacy-by-default for sensitive client files.',
  },
  {
    title: 'History + audit trail',
    body: 'Every review persisted: customer, score, decision, top failures, file hash, cost. Search past work. Export when needed.',
  },
  {
    title: 'Per-review cost transparency',
    body: 'See exact token spend and USD cost per review, broken down by module. Price your service confidently.',
  },
];

const REGS = ['MLR 2017', 'JMLSG', 'FATF R10', 'FATF R12', 'FATF R22', 'FATF R24', 'EU AMLD6'];

export default function Landing({ onSignIn }) {
  return (
    <div className="min-h-screen bg-navy-900 text-white">
      {/* ─── Top nav ─────────────────────────────────────────────── */}
      <header className="border-b border-navy-700 bg-navy-800">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-gold text-lg font-bold text-navy-900">
              R
            </div>
            <div>
              <div className="text-base font-semibold tracking-wide">RegDefence AI</div>
              <div className="text-xs text-slate-400">
                KYC/CDD QA &middot; Regulatory defensibility engine
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="#pricing"
              className="hidden text-xs text-slate-300 hover:text-white sm:inline"
            >
              Pricing
            </a>
            <a
              href="mailto:hello@cognitive-compliance.co.uk"
              className="hidden text-xs text-slate-300 hover:text-white sm:inline"
            >
              Contact
            </a>
            <button
              type="button"
              onClick={onSignIn}
              className="rounded-md bg-gold px-4 py-2 text-sm font-semibold text-navy-900 transition hover:brightness-110"
            >
              Sign in
            </button>
          </div>
        </div>
      </header>

      {/* ─── Hero ────────────────────────────────────────────────── */}
      <section className="border-b border-navy-700">
        <div className="mx-auto grid max-w-6xl gap-10 px-6 py-16 md:grid-cols-[1.2fr_1fr]">
          <div>
            <div className="mb-4 inline-block rounded-full border border-gold/40 bg-navy-800 px-3 py-1 text-xs font-medium text-gold">
              For UK MLROs, KYC analysts, and remediation programmes
            </div>
            <h1 className="mb-5 text-4xl font-semibold leading-tight md:text-5xl">
              Score a KYC file's <span className="text-gold">defensibility</span> in 90 seconds.
            </h1>
            <p className="mb-7 text-lg leading-relaxed text-slate-300">
              RegDefence AI reviews your CDD/EDD files against MLR 2017, JMLSG and FATF
              standards. It produces a 0–100 defensibility score, a ranked red-flag register,
              and a regulator-ready PDF report — built to withstand a Section 166 review.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={onSignIn}
                className="rounded-md bg-gold px-6 py-3 text-sm font-semibold text-navy-900 transition hover:brightness-110"
              >
                Sign in to start a review
              </button>
              <a
                href="#how-it-works"
                className="rounded-md border border-navy-700 px-6 py-3 text-sm text-slate-200 transition hover:border-gold hover:text-white"
              >
                How it works
              </a>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-2 text-xs text-slate-500">
              <span>References:</span>
              {REGS.map((r) => (
                <span
                  key={r}
                  className="rounded-full border border-navy-700 px-2 py-0.5 text-slate-400"
                >
                  {r}
                </span>
              ))}
            </div>
          </div>

          {/* Hero side card — sample output mock */}
          <div className="rounded-xl border border-navy-700 bg-navy-800 p-5 shadow-xl">
            <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-gold">
              Sample output
            </div>
            <div className="rounded-lg border border-navy-700 bg-navy-900 p-4">
              <div className="mb-1 text-xs text-slate-400">Acme Trading & Logistics Ltd</div>
              <div className="flex items-baseline gap-2">
                <div className="text-5xl font-bold text-red-400">28</div>
                <div className="text-sm text-slate-400">/ 100 · Indefensible</div>
              </div>
              <div className="mt-2 inline-block rounded-full border border-red-900/40 bg-red-900/10 px-2 py-0.5 text-xs font-medium text-red-300">
                Reject
              </div>
              <div className="mt-4 space-y-2 text-xs text-slate-300">
                <div className="border-l-2 border-red-700 pl-2">
                  <strong className="text-red-300">Critical:</strong> 75% controlling BVI shareholder undocumented (7 months).
                </div>
                <div className="border-l-2 border-amber-700 pl-2">
                  <strong className="text-amber-300">Material:</strong> Russian counterparties unscreened during active sanctions.
                </div>
                <div className="border-l-2 border-orange-700 pl-2">
                  <strong className="text-orange-300">Material:</strong> Risk rating Medium inadequate for cumulative factors.
                </div>
              </div>
            </div>
            <div className="mt-3 text-[10px] text-slate-500">
              Anonymised demonstration file. Real reviews use your KYC packs.
            </div>
          </div>
        </div>
      </section>

      {/* ─── How it works ───────────────────────────────────────── */}
      <section id="how-it-works" className="border-b border-navy-700">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <div className="mb-8 text-xs font-semibold uppercase tracking-wide text-gold">
            How it works
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {[
              { n: 1, h: 'Upload', b: 'Drag-drop a PDF/DOCX, paste text, or run on a built-in sample. Scanned PDFs trigger browser-side OCR.' },
              { n: 2, h: 'Six AI modules run in sequence', b: 'Fact extraction → KYC QA → red flags → defensibility → score → final report. Live progress, ~90 seconds end-to-end.' },
              { n: 3, h: 'Download + persist', b: 'Regulator-ready PDF and Word exports. Every review saved to your audit trail with score, decision, and cost.' },
            ].map((s) => (
              <div key={s.n} className="rounded-lg border border-navy-700 bg-navy-800 p-5">
                <div className="mb-3 flex h-8 w-8 items-center justify-center rounded-full bg-gold text-sm font-bold text-navy-900">
                  {s.n}
                </div>
                <div className="mb-2 text-sm font-semibold text-white">{s.h}</div>
                <p className="text-xs leading-relaxed text-slate-300">{s.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Features ───────────────────────────────────────────── */}
      <section className="border-b border-navy-700">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <div className="mb-8 text-xs font-semibold uppercase tracking-wide text-gold">
            What's in the box
          </div>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => (
              <div key={f.title} className="rounded-lg border border-navy-700 bg-navy-800 p-5">
                <div className="mb-2 text-sm font-semibold text-white">{f.title}</div>
                <p className="text-xs leading-relaxed text-slate-300">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Pricing teaser ─────────────────────────────────────── */}
      <section id="pricing" className="border-b border-navy-700">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <div className="mb-8 text-xs font-semibold uppercase tracking-wide text-gold">
            Pricing
          </div>
          <div className="grid gap-5 md:grid-cols-2">
            <div className="rounded-lg border border-gold/40 bg-navy-800 p-6">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gold">
                Starter pack
              </div>
              <div className="mb-2 text-3xl font-bold text-white">10 reviews</div>
              <div className="mb-4 text-2xl font-semibold text-white">
                £49 <span className="text-sm font-normal text-slate-400">/ pack</span>
              </div>
              <ul className="mb-6 space-y-1.5 text-sm text-slate-200">
                <li>• Full 6-module pipeline per review</li>
                <li>• PDF + Word regulator-ready report</li>
                <li>• Persistent review history</li>
                <li>• 90-day credit expiry</li>
              </ul>
              <button
                type="button"
                onClick={onSignIn}
                className="w-full rounded-md bg-gold px-4 py-2 text-sm font-semibold text-navy-900 transition hover:brightness-110"
              >
                Sign in to buy
              </button>
            </div>
            <div className="rounded-lg border border-navy-700 bg-navy-800 p-6">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                Volume / annual
              </div>
              <div className="mb-2 text-3xl font-bold text-white">Talk to us</div>
              <ul className="mb-6 space-y-1.5 text-sm text-slate-200">
                <li>• Volume pricing ladders</li>
                <li>• Per-user accounts (instead of shared password)</li>
                <li>• Custom prompt tuning to your firm's QA framework</li>
                <li>• DPA + SOC2-equivalent posture</li>
              </ul>
              <a
                href="mailto:hello@cognitive-compliance.co.uk?subject=RegDefence%20AI%20%E2%80%93%20enterprise%20enquiry"
                className="block w-full rounded-md border border-gold px-4 py-2 text-center text-sm font-semibold text-gold transition hover:bg-gold hover:text-navy-900"
              >
                Email us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Closing CTA ────────────────────────────────────────── */}
      <section>
        <div className="mx-auto max-w-3xl px-6 py-16 text-center">
          <h2 className="mb-3 text-2xl font-semibold">
            Built by Cognitive Compliance Limited
          </h2>
          <p className="mb-6 text-sm text-slate-300">
            Independent UK financial-crime consultancy. We've remediated thousands of files
            across challenger banks, EMIs, and crypto firms. RegDefence is the QA tool we
            wished we had.
          </p>
          <button
            type="button"
            onClick={onSignIn}
            className="rounded-md bg-gold px-6 py-3 text-sm font-semibold text-navy-900 transition hover:brightness-110"
          >
            Sign in
          </button>
        </div>
      </section>

      {/* ─── Footer ──────────────────────────────────────────────── */}
      <footer className="border-t border-navy-700 bg-navy-800">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-6 py-6 text-xs text-slate-400 sm:flex-row">
          <div>© {new Date().getFullYear()} Cognitive Compliance Limited</div>
          <div className="flex gap-4">
            <a href="mailto:hello@cognitive-compliance.co.uk" className="hover:text-white">Contact</a>
            <a href="https://cognitive-compliance.co.uk" className="hover:text-white">cognitive-compliance.co.uk</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
